/**
 * town_layout.ts — the deterministic TOWN PLAN every settlement shares:
 * named, functional building slots spread across the WHOLE footprint
 * (SETTLEMENT_FOOTPRINT — city 240 m, town 120, hamlet 70), so daily life
 * crosses the town instead of orbiting a 20 m disc (the pacing root cause:
 * seeding ringed homes at 8 m and work at 20 m).
 *
 * Pure function of (seed, site) — the server drives NPC movement to these
 * slots and the client labels them, so both derive the SAME positions.
 * CLIENT MIRROR of server/src/lib/town_layout.ts (byte-equivalent tables + math).
 */
import { SETTLEMENT_FOOTPRINT, settlementRng, settlementSites, type SettlementSite } from './settlements.js';
import { CORE_NAMES, capitalDistrictOf } from './capitals.js';

export interface TownSlot {
  key: string;
  /** Engine 33-fn taxonomy subset (matches workplaceFunction). */
  fn: string;
  /** Venue name in each civilization's voice (a = Aurelian, b = Borean). */
  nameA: string;
  nameB: string;
  x: number;
  z: number;
}

/** fn → [Aurelian voice, Borean voice] name templates ({E} = site epithet). */
const SLOT_DEFS: Array<{ key: string; fn: string; a: string; b: string }> = [
  { key: 'plaza', fn: 'civic', a: '{E} Concourse', b: '{E} Gatherhall' },
  { key: 'market', fn: 'market', a: 'The Gilded Scale of {E}', b: '{E} Tradeworks' },
  { key: 'tavern', fn: 'entertainment', a: 'The Radiant Cup', b: 'The Frosthearth' },
  { key: 'forge', fn: 'manufacturing', a: '{E} Fabrication Halls', b: '{E} Ironworks' },
  { key: 'granary', fn: 'agriculture', a: 'The {E} Growing Terraces', b: '{E} Provision Vaults' },
  { key: 'well', fn: 'civic', a: 'The Sunwell', b: 'The Deepspring' },
  { key: 'shrine', fn: 'faith', a: 'Shrine of the Dawn Accord', b: 'The Aurora Sanctum' },
  { key: 'guardpost', fn: 'defense', a: '{E} Warden Post', b: '{E} Watchspire' },
  { key: 'stage', fn: 'entertainment', a: 'The {E} Leisure Spire', b: '{E} Songvault' },
  { key: 'assay', fn: 'industry', a: '{E} Ore Works', b: '{E} Boreworks' },
  { key: 'archive', fn: 'science', a: 'The {E} Survey Annex', b: '{E} Charthouse' },
  { key: 'bathhouse', fn: 'civic', a: 'The Amber Baths', b: 'The Steamhalls' },
];

/** Slots per tier (first N of SLOT_DEFS — plaza always first, at the center). */
const TIER_SLOTS: Record<SettlementSite['tier'], number> = { city: 12, town: 8, hamlet: 5, capital: 12, district: 12, circuit: 8 };

const EPITHETS = [
  'Meridian', 'Solace', 'Vantage', 'Halcyon', 'Kestrel', 'Auric', 'Vesper', 'Cinder',
  'Lattice', 'Harbor', 'Zenith', 'Thistle', 'Ember', 'Corvid', 'Argent', 'Bastion',
  'Drift', 'Fable', 'Garnet', 'Hollow', 'Isle', 'Juniper', 'Cairn', 'Loom',
];

/** The named functional slots of one settlement, spread across its footprint
 *  on a golden-angle spiral (plaza pinned at the center). */
export function townSlots(seed: string, site: SettlementSite): TownSlot[] {
  if (site.tier === 'capital') return capitalSlots(seed, site); // phase 4: the district union
  const rng = settlementRng(`${seed}|town|${site.siteSeed}`);
  const footprint = SETTLEMENT_FOOTPRINT[site.tier];
  const epithet = EPITHETS[Math.floor(rng() * EPITHETS.length) % EPITHETS.length];
  const baseAng = rng() * Math.PI * 2;
  const count = TIER_SLOTS[site.tier];
  const out: TownSlot[] = [];
  for (let i = 0; i < count; i++) {
    const def = SLOT_DEFS[i];
    // Plaza holds the center; the rest spiral outward to 85% of the footprint.
    const t = i === 0 ? 0 : (i - 1) / Math.max(1, count - 2);
    const r = i === 0 ? 0 : footprint * (0.3 + 0.55 * t) * (0.9 + rng() * 0.2);
    const ang = baseAng + i * 2.399963;
    out.push({
      key: def.key,
      fn: def.fn,
      nameA: def.a.replace('{E}', epithet),
      nameB: def.b.replace('{E}', epithet),
      x: site.centerX + Math.cos(ang) * r,
      z: site.centerZ + Math.sin(ang) * r,
    });
  }
  return out;
}

/** The slot a JOB works at (falls back to the plaza). */
export function jobSlotKey(job: string): string {
  switch (job) {
    case 'merchant': return 'market';
    case 'smith': return 'forge';
    case 'guard': return 'guardpost';
    case 'bard': return 'stage';
    case 'farmer': return 'granary';
    case 'miner': return 'assay';
    case 'scout': return 'archive';
    // society wave (2026-09-19): the producer trades without a node of their own
    case 'fisher': case 'forester': case 'tapper': return 'granary';
    case 'gas_tender': return 'assay';
    case 'hunter': return 'guardpost';
    case 'herbalist': return 'archive';
    default: return 'plaza';
  }
}

export function slotByKey(slots: TownSlot[], key: string): TownSlot {
  return slots.find((s) => s.key === key) ?? slots[0];
}


// ── Capital wave (phase 4): the district-union venue slots ──────────────────
const UNION_RE = /^d\d\d:/;

/** A capital's venue slots: its own plaza (the civic core) followed by the UNION
 *  of its thirty districts' slot sets — each district's own 12-slot spiral,
 *  keyed `d<NN>:<key>` in district order — so `resolveSlot` can hand 600
 *  residents thirty wards' worth of homes, jobs and squares. */
function capitalSlots(seed: string, site: SettlementSite): TownSlot[] {
  const out: TownSlot[] = [{ key: 'plaza', fn: 'civic', nameA: CORE_NAMES.a, nameB: CORE_NAMES.b, x: site.centerX, z: site.centerZ }];
  const districts = settlementSites(seed).filter((s) => s.tier === 'district' && s.capitalId === site.id).sort((a, b) => (a.district ?? 0) - (b.district ?? 0));
  for (const d of districts) {
    const nn = String(d.district ?? 0).padStart(2, '0');
    for (const s of townSlots(seed, d)) out.push({ ...s, key: `d${nn}:${s.key}` });
  }
  return out;
}

/** Is this slot list a capital's district union? */
export function isSlotUnion(slots: TownSlot[]): boolean {
  return slots.length > 1 && UNION_RE.test(slots[1].key);
}
/** The number of districts a union carries. */
export function unionDistricts(slots: TownSlot[]): number {
  const seen = new Set<string>();
  for (const s of slots) if (UNION_RE.test(s.key)) seen.add(s.key.slice(0, 3));
  return seen.size;
}
/**
 * A slot by key. For a capital's UNION the district comes from the salt (the
 * agent id for homes and jobs, the event's host for ceremonies): `d<NN>:<key>`,
 * falling back to that district's plaza. City / town / hamlet lists resolve
 * exactly as `slotByKey` does — byte-identical behaviour.
 */
export function resolveSlot(slots: TownSlot[], key: string, salt: bigint | number): TownSlot {
  if (!isSlotUnion(slots)) return slotByKey(slots, key);
  const p = `d${String(capitalDistrictOf(unionDistricts(slots), salt)).padStart(2, '0')}:`;
  return slots.find((s) => s.key === p + key) ?? slots.find((s) => s.key === p + 'plaza') ?? slots[0];
}
/** Where a resident's HOME is drawn around: a capital resident's district (by the
 *  same salt `resolveSlot` uses, so home and work share a ward) at a district's
 *  footprint; every other tier its own centre and footprint. */
export function homeAnchor(seed: string, site: SettlementSite, salt: bigint | number): { x: number; z: number; fp: number } {
  if (site.tier === 'capital') {
    const districts = settlementSites(seed).filter((s) => s.tier === 'district' && s.capitalId === site.id).sort((a, b) => (a.district ?? 0) - (b.district ?? 0));
    if (districts.length > 0) {
      const d = districts[capitalDistrictOf(districts.length, salt)];
      return { x: d.centerX, z: d.centerZ, fp: SETTLEMENT_FOOTPRINT.district };
    }
  }
  return { x: site.centerX, z: site.centerZ, fp: SETTLEMENT_FOOTPRINT[site.tier] };
}

/** The i-th slot key of the town plan (the top-up hands newcomers slots by index). */
export function slotKeyAt(i: number): string {
  return SLOT_DEFS[((i % SLOT_DEFS.length) + SLOT_DEFS.length) % SLOT_DEFS.length].key;
}

/** Off-hours social venues, rotated deterministically per epoch. */
export const SOCIAL_SLOT_KEYS = ['tavern', 'well', 'shrine', 'stage'] as const;
